import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetCountryListForDropdowns } from "../../../../api/services/client/clientApis";
import {
  useGetCompatibleProgramTemplates,
  useGetDestinationPrograms,
  useGetProgramDetailById,
  useProgramTransfer,
} from "../../../../api/services/program/programApis";
import {
  FormController,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiRadioGroup,
  MuiSelect,
  MuiTypography,
  OptionsDataType,
} from "../../../../components";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import { PAGE_URLS } from "../../../../helpers/constants";
import {
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { CancelFieldModal } from "../../../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import { DurationWarningModal } from "../../../createProgramTemplate/components/durationWarningModal/DurationWarningModal";
import {
  TransferParticipantFormKey,
  transferTypeKey,
} from "../../modelsAndSchemas/models";
import { TransferParticipantSchema } from "../../modelsAndSchemas/ValidationSchema";
import { programSearchSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { TransferProgramDetails } from "../transferProgramDetails/TransferProgramDetails";
import { InputTransferDetailsWrapper } from "./InputTransferDetails.style";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useGetNextLanguages } from "../../../../api/services/bulkTransferParticipants/bulkTransferParticipants";
import { UserCountry } from "../../../systemMessageSearch/modelsAndSchemas/models";

interface InputTransferDetailsProps {
  pageId: string;
  open: boolean;
  setShowInputTransferDetailsModal: (value: boolean) => void;
  programId?: string;
  participantName: string;
}

export const InputTransferDetails: FC<InputTransferDetailsProps> = ({
  open,
  pageId,
  setShowInputTransferDetailsModal,
  programId,
  participantName,
}) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const defaultValues = {
    [TransferParticipantFormKey.destinationCountry]: "",
    [TransferParticipantFormKey.compatibleProgramTemplate]: "",
    [TransferParticipantFormKey.nextLanguage]: "",
    [TransferParticipantFormKey.transferType]: transferTypeKey.changeCountry,
  };
  const {
    commonLabels,
    fetchComponentTitle,
    fetchLabel,
    fetchValidationMessage,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const {
    data: countryListData,
    isFetching: countryPending,
    isError: countryError,
  } = useGetCountryListForDropdowns();

  const {
    mutateAsync: saveProgramTransferApi,
    isPending: isProgramTransferApi,
    isError: isProgramTransferError,
    isSuccess: isProgramTransferSuccess,
    data: programTransferData,
  } = useProgramTransfer();

  const {
    data: programDetails,
    isFetching: isViewProgramDetailFetching,
    isError: isViewProgramDetailError,
    refetchWithParams: refetchViewProgramDetail,
  } = useGetProgramDetailById();

  const {
    data: destinationData,
    isError: isDestinationProgramsError,
    isFetching: isGetDestinationProgramsFetching,
    refetchWithParams: refetchGetDestinationPrograms,
  } = useGetDestinationPrograms();

  const {
    data: compatibleProgramTemplateData,
    refetchWithParams: refetchCompatibleProgramTemplate,
  } = useGetCompatibleProgramTemplates();

  const {
    isFetching: isNextLanguagePending,
    data: nextLanguageData,
    isError: isNextLanguageError,
    refetchWithParams: refetchNextLanguages,
  } = useGetNextLanguages();

  const isCMC = programDetails?.solutionOffering === "CMC";
  const {
    setValue,
    getValues,
    control,
    watch,
    reset,
    formState: { isDirty },
    handleSubmit,
  } = useForm({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(
      TransferParticipantSchema(fetchValidationMessage, isCMC),
    ),
  });

  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const getLabel = (key: string) => {
    return fetchLabel(programSearchSiteCoreKeys.transferParticipantPopup, key);
  };

  const radioOptions = [
    {
      value: transferTypeKey.changeCountry,
      label: getLabel(programSearchSiteCoreKeys.changeCountry),
    },
    {
      value: transferTypeKey.changeTransfer,
      label: getLabel(programSearchSiteCoreKeys.changeProgram),
    },
  ];
  const formatName = (input: string) => input.replace(/,/g, "").trim();
  useEffect(() => {
    const destinationCountry = getValues(
      TransferParticipantFormKey.destinationCountry,
    );
    const compatibleProgramTemplate = getValues(
      TransferParticipantFormKey.compatibleProgramTemplate,
    );
    if (destinationCountry) {
      refetchGetDestinationPrograms(programId ?? "", destinationCountry, true);
      refetchNextLanguages(destinationCountry);
      setValue(TransferParticipantFormKey.nextLanguage, "");
    } else {
      refetchGetDestinationPrograms(
        programId ?? "",
        compatibleProgramTemplate ?? "",
        false,
      );
    }
  }, [
    watch(TransferParticipantFormKey.destinationCountry),
    watch(TransferParticipantFormKey.compatibleProgramTemplate),
  ]);

  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    reset(defaultValues);
    const transferType = e.target.value;
    setValue(TransferParticipantFormKey.transferType, transferType);
  };

  useEffect(() => {
    if (isDestinationProgramsError) {
      setShowWarningModal(true);
    }
  }, [isDestinationProgramsError]);
  useEffect(() => {
    if (
      isViewProgramDetailError ||
      countryError ||
      isProgramTransferError ||
      isNextLanguageError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isViewProgramDetailError,
    countryError,
    isProgramTransferError,
    isNextLanguageError,
  ]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isGetDestinationProgramsFetching ||
          isViewProgramDetailFetching ||
          countryPending ||
          isProgramTransferApi ||
          isNextLanguagePending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isGetDestinationProgramsFetching,
    isViewProgramDetailFetching,
    countryPending,
    isProgramTransferApi,
    isNextLanguagePending,
  ]);

  useEffect(() => {
    if (isProgramTransferSuccess) {
      setShowSaveModal(false);
      setShowInputTransferDetailsModal(false);
      reset(defaultValues);
      navigate(
        `${PAGE_URLS.EDIT_PROGRAM}/${programTransferData?.data?.id ?? ""}`,
        {
          state: {
            id: programTransferData?.data?.id,
            isTransferedProgram: programTransferData?.data?.isTransferedProgram,
          },
        },
      );
    }
  }, [isProgramTransferSuccess]);

  useEffect(() => {
    refetchViewProgramDetail(programId ?? "");
    refetchCompatibleProgramTemplate(programId ?? "");
  }, []);

  useEffect(() => {
    setValue(
      TransferParticipantFormKey.transferType,
      transferTypeKey.changeCountry,
    );
  }, [setValue]);

  const saveButton = () => {
    setShowSaveModal(true);
  };
  const onCloseHandler = () => {
    if (isDirty) {
      setShowCancelModal(true);
    } else {
      setShowInputTransferDetailsModal(false);
      reset(defaultValues);
    }
  };
  const onYesHandler = () => {
    setShowCancelModal(false);
    setShowInputTransferDetailsModal(false);
    reset(defaultValues);
  };

  const onProceedHandler = () => {
    saveProgramTransferApi({
      params: {
        ...(getValues(TransferParticipantFormKey.destinationCountry)
          ? {
              countryCode: destinationData?.destination?.countryCode,
              languageCode: getValues(TransferParticipantFormKey.nextLanguage),
            }
          : {
              programTemplateId: destinationData?.compatibleProgramTemplate?.id,
            }),
        ...{ programId: programId ?? "" },
      },
    });
  };

  const nextLanguageOptions = useMemo(() => {
    let nextLanguages: OptionsDataType[] = [];
    if (isNotEmpty(nextLanguageData)) {
      nextLanguages = nextLanguageData.map((country: UserCountry) => ({
        label: country.localeDescription,
        value: country.localeName,
      }));
    }
    return nextLanguages;
  }, [nextLanguageData]);

  const renderFormController = (
    formName: string,
    label: string,
    optionsValue: any[],
  ) => {
    return (
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={formName}>
          <MuiSelect
            label={getLabel(label)}
            options={optionsValue}
            data-testid="destinnation-country"
            visibleDefaultSelectOption
            value=""
            id={`${pageId}-${getValues(TransferParticipantFormKey.transferType)}`}
            isMandatory
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            showSearch
          />
        </FormController>
      </MuiGrid>
    );
  };

  const watchTransferType = watch(TransferParticipantFormKey.transferType);
  const selectedFormController = useMemo(() => {
    return watchTransferType === transferTypeKey.changeCountry
      ? renderFormController(
          TransferParticipantFormKey.destinationCountry,
          programSearchSiteCoreKeys.destinationCountry,
          setApiDataToDropDownOption(
            countryListData,
            true,
            programDetails?.deliveryCountryName,
          ),
        )
      : renderFormController(
          TransferParticipantFormKey.compatibleProgramTemplate,
          programSearchSiteCoreKeys.compatibleProgramTemplate,
          setApiDataToDropDownOption(compatibleProgramTemplateData),
        );
  }, [watchTransferType, countryListData, programDetails?.deliveryCountryName]);

  const nextLanguageWatch = useWatch({
    name: TransferParticipantFormKey.nextLanguage,
    control,
  });
  const getSelectedNextLanguageLabel = useMemo(() => {
    if (watchTransferType === transferTypeKey.changeCountry) {
      const selectedLanguage = getValues(
        TransferParticipantFormKey.nextLanguage,
      );
      const selectedLanguageObj = nextLanguageOptions.find(
        (lang) => lang.value === selectedLanguage,
      );
      return selectedLanguageObj ? selectedLanguageObj.label : "";
    } else {
      return "";
    }
  }, [watchTransferType, nextLanguageWatch]);

  const renderLanguageDropdown = useCallback(() => {
    return (
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={TransferParticipantFormKey.nextLanguage}
        >
          <MuiSelect
            label={getLabel(programSearchSiteCoreKeys.nextLanguage)}
            options={nextLanguageOptions}
            visibleDefaultSelectOption
            value=""
            id={`${pageId}-${TransferParticipantFormKey.nextLanguage}`}
            isMandatory
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            showSearch
          />
        </FormController>
      </MuiGrid>
    );
  }, [watchTransferType, nextLanguageOptions]);

  return (
    <MuiModal
      onClose={onCloseHandler}
      title={fetchComponentTitle(
        programSearchSiteCoreKeys.transferParticipantPopup,
      )}
      open={open}
      primaryButtonTitle={commonLabels.save}
      secondaryButtonTitle={commonLabels.cancel}
      hasLargeContent
      onSecondaryButtonClick={onCloseHandler}
      onPrimaryButtonClick={handleSubmit(saveButton)}
    >
      <InputTransferDetailsWrapper>
        <form data-testid="program-form" className="program-form">
          {isCMC ? (
            <>
              {/* Radio Group for CM Users */}
              <MuiBox className="radio-button-container">
                <FormController
                  control={control}
                  name={TransferParticipantFormKey.transferType}
                >
                  <MuiRadioGroup
                    label={getLabel(programSearchSiteCoreKeys.transferType)}
                    value={
                      getValues(TransferParticipantFormKey.transferType) ?? ""
                    }
                    onChange={handleSearchTypeChange}
                    options={radioOptions}
                    data-testid="destinnation-country"
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiBox>
              <MuiGrid container>
                {selectedFormController}
                {watchTransferType === transferTypeKey.changeCountry &&
                  renderLanguageDropdown()}
              </MuiGrid>
            </>
          ) : (
            /* If IT is a OP and TM */
            <MuiGrid container>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <FormController
                  control={control}
                  name={TransferParticipantFormKey.destinationCountry}
                >
                  <MuiSelect
                    label={getLabel(
                      programSearchSiteCoreKeys.destinationCountry,
                    )}
                    data-testid="destinnation-country"
                    options={setApiDataToDropDownOption(
                      countryListData,
                      true,
                      programDetails?.deliveryCountryName,
                    )}
                    visibleDefaultSelectOption
                    value=""
                    id={`${pageId}-${TransferParticipantFormKey.destinationCountry}`}
                    isMandatory
                    placeholder={commonLabels.selectPlaceHolder}
                    requiredARIALabel={commonARIALabel.required}
                    showSearch
                    searchPlaceholder={commonLabels.search}
                  />
                </FormController>
              </MuiGrid>
              {renderLanguageDropdown()}
            </MuiGrid>
          )}
        </form>
        <MuiBox className="participant-details">
          <MuiTypography
            variant="h2SemiBold"
            className="participant-details-label"
          >
            {getLabel(programSearchSiteCoreKeys.participantDetails)}
          </MuiTypography>
        </MuiBox>
        <MuiGrid container className="participant-container">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSearchSiteCoreKeys.participantName)}
              value={formatName(participantName ?? "")}
              isValueWithEllipsis
              replaceEmptyWithDash
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(
                programSearchSiteCoreKeys.programKeyInputTransfer,
              )}
              value={programDetails?.keyNumber}
              isValueWithEllipsis
              replaceEmptyWithDash
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSearchSiteCoreKeys.programStatus)}
              value={<StatusBadge status={"Status.Active"} label={"Active"} />}
              isValueWithEllipsis
              replaceEmptyWithDash
            />
          </MuiGrid>
        </MuiGrid>
        <MuiBox className="dot-container">
          <TransferProgramDetails
            pageId={pageId}
            sourceProgramData={programDetails}
            destinationProgramData={{
              ...destinationData,
              selectedNextLanguage: getSelectedNextLanguageLabel,
            }}
            isTransferToCountry={
              watchTransferType === transferTypeKey.changeCountry
            }
          />
        </MuiBox>
        {showCancelModal && (
          <CancelFieldModal
            showModal={showCancelModal}
            handleModalClose={() => setShowCancelModal(false)}
            handlePrimaryButtonClick={onYesHandler}
          />
        )}
        {showWarningModal && (
          <DurationWarningModal
            handleModalClose={() => {
              setShowWarningModal(false);
              reset(defaultValues);
            }}
            showModal={showWarningModal}
            label={getLabel(programSearchSiteCoreKeys.supportTicket)}
          />
        )}
        {showSaveModal && (
          <CancelFieldModal
            title={commonLabels.information}
            primaryButtonTitle={getLabel(programSearchSiteCoreKeys.proceed)}
            cancelMessage={getLabel(programSearchSiteCoreKeys.processUpdate)}
            secondaryButtonTitle={"Cancel Transfer"}
            showModal={showSaveModal}
            handleModalClose={() => setShowSaveModal(false)}
            handlePrimaryButtonClick={onProceedHandler}
          />
        )}
      </InputTransferDetailsWrapper>
    </MuiModal>
  );
};
